import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../components/ui';
import { Button, Card, Badge, Input, Modal, Spinner } from '../../components/ui';
import { 
  Plus, Edit2, Trash2, Package, Tag, ShoppingBag, 
  DollarSign, Box, Upload, Cloud, Trash, Search,
  Filter, Grid, List, X, Image as ImageIcon,
  AlertCircle, CheckCircle, Clock, Zap
} from 'lucide-react';
import './Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    stockQuantity: '',
    images: [],
    categoryId: ''
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const { request } = useApi();
  const { success, error: toastError } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.all([
        request('get', '/api/products').catch(() => []),
        request('get', '/api/products/categories').catch(() => [])
      ]);
      setProducts(productsData || []);
      setCategories(categoriesData || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditing(null);
    setForm({ name: '', description: '', price: '', stockQuantity: '', images: [], categoryId: '' });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditing(product);
    setForm({
      name: product.name,
      description: product.description || '',
      price: product.price,
      stockQuantity: product.stockQuantity,
      images: product.images || [],
      categoryId: product.category?.id || ''
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const body = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      stockQuantity: parseInt(form.stockQuantity) || 0,
      images: form.images,
      isActive: true
    };

    try {
      let savedProduct;
      if (editing) {
        savedProduct = await request('put', `/api/products/${editing.id}?categoryId=${form.categoryId || ''}`, body);
      } else {
        savedProduct = await request('post', `/api/products?categoryId=${form.categoryId || ''}`, body);
      }

      setShowModal(false);
      await fetchData();
      success(editing ? 'Product updated successfully!' : 'Product created successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save product');
      toastError(err.response?.data?.error || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await request('post', '/api/products/categories', categoryForm);
      setShowCategoryModal(false);
      setCategoryForm({ name: '', description: '' });
      fetchData();
      success('Category created successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product? This action cannot be undone.')) return;
    try {
      await request('delete', `/api/products/${productId}`);
      fetchData();
      success('Product deleted successfully!');
    } catch (err) {
      console.error('Failed to delete product:', err);
      toastError('Failed to delete product');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Products will remain without a category.')) return;
    try {
      await request('delete', `/api/products/categories/${categoryId}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const productId = editing?.id;
    if (!productId) {
      setError('Please save the product first before uploading images');
      e.target.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      e.target.value = '';
      return;
    }

    setUploadingImage(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await request('post', `/api/products/${productId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.success && response.url) {
        const imageUrl = response.url;

        setForm(prev => ({
          ...prev,
          images: [...prev.images, imageUrl]
        }));

        setEditing(prev => ({
          ...prev,
          images: [...(prev.images || []), imageUrl]
        }));

        setProducts(prev => prev.map(p =>
          p.id === productId
            ? { ...p, images: [...(p.images || []), imageUrl] }
            : p
        ));

        success('Image uploaded successfully!');
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
      toastError(err.message || 'Upload failed');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleDeleteImage = async (productId, imageUrl) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      await request('delete', `/api/products/${productId}/image?imageUrl=${encodeURIComponent(imageUrl)}`);

      setForm(prev => ({
        ...prev,
        images: prev.images.filter(img => img !== imageUrl)
      }));

      setEditing(prev => ({
        ...prev,
        images: (prev.images || []).filter(img => img !== imageUrl)
      }));

      setProducts(prev => prev.map(p =>
        p.id === productId
          ? { ...p, images: (p.images || []).filter(img => img !== imageUrl) }
          : p
      ));

      success('Image deleted successfully!');
    } catch (err) {
      setError(err.message || 'Delete failed');
      toastError(err.message || 'Delete failed');
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalProducts = products.length;
  const totalStock = products.reduce((sum, p) => sum + (p.stockQuantity || 0), 0);
  const totalValue = products.reduce((sum, p) => sum + (parseFloat(p.price) * (p.stockQuantity || 0)), 0);

  if (loading) {
    return (
      <div className="products-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="products-header">
        <div>
          <h1 className="products-title">Products</h1>
          <p className="products-subtitle">Manage your product catalog and inventory</p>
        </div>
        <div className="header-actions">
          <Button variant="outline" onClick={() => setShowCategoryModal(true)}>
            <Tag size={16} /> Categories
          </Button>
          <Button onClick={openCreateModal} className="btn-create">
            <Plus size={16} /> Add Product
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="products-stats">
        <div className="stat-card">
          <div className="stat-icon purple"><Package size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{totalProducts}</div>
            <div className="stat-label">Total Products</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue"><Box size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{totalStock.toLocaleString()}</div>
            <div className="stat-label">Total Stock</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><DollarSign size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">TZS {totalValue.toLocaleString()}</div>
            <div className="stat-label">Inventory Value</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><Tag size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{categories.length}</div>
            <div className="stat-label">Categories</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="products-toolbar">
        <div className="search-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="view-toggle">
          <button 
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <Grid size={16} />
          </button>
          <button 
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="empty-state">
          <ShoppingBag size={64} className="empty-icon" />
          <h3>No products found</h3>
          <p>
            {searchTerm 
              ? 'Try adjusting your search terms'
              : 'Start adding your products to showcase them to customers'
            }
          </p>
          {!searchTerm && (
            <Button onClick={openCreateModal}>Add Your First Product</Button>
          )}
        </div>
      ) : (
        <div className={`products-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
          {filteredProducts.map((product) => (
            <div key={product.id} className="product-card-wrapper">
              <div className="product-image">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.nextSibling;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="image-placeholder"
                  style={{ display: product.images && product.images.length > 0 ? 'none' : 'flex' }}
                >
                  <ShoppingBag size={32} />
                </div>
                {product.stockQuantity <= 10 && product.stockQuantity > 0 && (
                  <div className="stock-badge low">Low Stock</div>
                )}
                {product.stockQuantity === 0 && (
                  <div className="stock-badge out">Out of Stock</div>
                )}
              </div>
              <div className="product-info">
                <div className="product-header">
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-actions">
                    <button className="action-btn edit" onClick={() => openEditModal(product)}>
                      <Edit2 size={14} />
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(product.id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {product.category && (
                  <div className="product-category">
                    <Tag size={12} />
                    <span>{product.category.name}</span>
                  </div>
                )}
                <p className="product-description">{product.description?.slice(0, 80)}</p>
                <div className="product-footer">
                  <div className="product-price">
                    <span className="price-label">Price</span>
                    <span className="price-value">TZS {Number(product.price).toLocaleString()}</span>
                  </div>
                  <div className="product-stock">
                    <span className="stock-label">Stock</span>
                    <span className={`stock-value ${product.stockQuantity <= 10 ? 'low' : ''}`}>
                      {product.stockQuantity} units
                    </span>
                  </div>
                </div>
                <div className="product-status">
                  <Badge variant={product.isActive ? 'success' : 'default'}>
                    {product.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Product' : 'Add Product'}>
        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Product Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Product name"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Product description"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Price (TZS)</label>
              <Input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="0"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Stock Quantity</label>
              <Input
                type="number"
                value={form.stockQuantity}
                onChange={(e) => setForm({ ...form, stockQuantity: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>

          {/* Image Upload */}
          <div className="form-group">
            <label className="form-label">Product Images</label>

            {editing ? (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <label
                    htmlFor="image-upload"
                    className="upload-btn"
                    style={{
                      padding: '10px 20px',
                      background: uploadingImage ? '#94A3B8' : '#2563EB',
                      color: 'white',
                      borderRadius: '12px',
                      cursor: uploadingImage ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    {uploadingImage
                      ? 'Uploading...'
                      : <><Upload size={16} /> Upload Image</>
                    }
                  </label>
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{ display: 'none' }}
                  />
                </div>

                {form.images.length > 0 && (
                  <div className="image-thumbnails">
                    {form.images.map((img, idx) => (
                      <div key={idx} className="image-thumbnail">
                        <img src={img} alt={`Product ${idx + 1}`} />
                        <button
                          type="button"
                          onClick={() => editing?.id && handleDeleteImage(editing.id, img)}
                          className="image-delete"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="image-placeholder-message">
                <Cloud size={24} style={{ opacity: 0.4 }} />
                <div>Save the product first, then you can upload images</div>
              </div>
            )}
          </div>

          {error && <div className="alert error">{error}</div>}

          <div className="modal-actions">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" loading={submitting}>
              {editing ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Category Modal */}
      <Modal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)} title="Manage Categories">
        <div className="categories-section">
          <div className="categories-list">
            <h4 className="categories-title">Existing Categories</h4>
            {categories.length === 0 ? (
              <div className="empty-categories">
                <Tag size={32} className="empty-icon" />
                <p>No categories yet</p>
              </div>
            ) : (
              categories.map(cat => (
                <div key={cat.id} className="category-item">
                  <div>
                    <div className="category-name">{cat.name}</div>
                    <div className="category-desc">{cat.description || 'No description'}</div>
                  </div>
                  <button className="delete-cat-btn" onClick={() => handleDeleteCategory(cat.id)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="category-divider"></div>
          <form onSubmit={handleCreateCategory} className="category-form">
            <h4 className="categories-title">Add New Category</h4>
            <div className="form-group">
              <label className="form-label">Category Name</label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Electronics, Clothing"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description (optional)</label>
              <textarea
                className="form-textarea"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Brief description"
                rows={2}
              />
            </div>
            <div className="modal-actions">
              <Button type="button" variant="ghost" onClick={() => setShowCategoryModal(false)}>Close</Button>
              <Button type="submit" loading={submitting}>Add Category</Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default Products;